import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { saTodayISO, formatDate } from '../../lib/dateUtils';
import { API_URL, auth, errMsg } from './financeApi';
import { EftNotice } from './EftNotice';

export const PayDialog = ({ open, onOpenChange, status, token, onDone, renew = false }) => {
  const [picked, setPicked] = useState([]);
  const [reference, setReference] = useState('');
  const [datePaid, setDatePaid] = useState(saTodayISO());
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const vehicles = (status?.vehicles || []).filter((v) => v.pending_payments === 0);

  useEffect(() => {
    if (open) {
      const due = vehicles.filter((v) => v.plan_active && v.plan_days_left <= 30).map((v) => v.vehicle_id);
      setPicked(renew && due.length ? due : vehicles.length === 1 ? [vehicles[0].vehicle_id] : []);
      setReference(''); setProof(null); setDatePaid(saTodayISO());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    const form = new FormData();
    form.append('vehicle_ids', picked.join(','));
    form.append('eft_reference', reference);
    form.append('date_paid', datePaid);
    form.append('proof', proof);
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/finance/payments`, form, auth(token));
      toast.success(res.data.message);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not send proof of payment'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pay-dialog">
        <DialogHeader>
          <DialogTitle>Pay by EFT and send proof</DialogTitle>
          <DialogDescription>One payment of R199 per vehicle covers 12 months. Your plan starts once the IT Administrator confirms the money was received.</DialogDescription>
        </DialogHeader>
        <EftNotice />
        <div className="space-y-4">
          <div>
            <Label>Vehicles to pay for</Label>
            {vehicles.length === 0 && <p className="text-sm text-muted-foreground mt-1" data-testid="pay-no-vehicles">All your vehicles already have a payment waiting for IT.</p>}
            <div className="space-y-2 mt-2">
              {vehicles.map((v) => (
                <label key={v.vehicle_id} className="flex items-center gap-3 border rounded-lg p-3 cursor-pointer">
                  <Checkbox checked={picked.includes(v.vehicle_id)} onCheckedChange={() => toggle(v.vehicle_id)} data-testid={`pay-vehicle-${v.registration_number}`} />
                  <span className="font-medium">{v.registration_number}</span>
                  <span className="text-xs text-muted-foreground">{v.plan_active ? `Active until ${formatDate(v.plan_expires_at)} — renewal adds 12 months` : 'No active plan'}</span>
                </label>
              ))}
            </div>
            <p className="text-sm font-semibold mt-2" data-testid="pay-total">Total to pay: R{199 * picked.length}</p>
          </div>
          <div>
            <Label htmlFor="eft-ref">EFT payment reference</Label>
            <Input id="eft-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. NY-MNM954GP" data-testid="pay-reference-input" />
            <p className="text-xs text-muted-foreground mt-1">Tip: use your vehicle registration as the reference.</p>
          </div>
          <div>
            <Label htmlFor="eft-date">Date paid</Label>
            <Input id="eft-date" type="date" max={saTodayISO()} value={datePaid} onChange={(e) => setDatePaid(e.target.value)} data-testid="pay-date-input" />
          </div>
          <div>
            <Label htmlFor="eft-proof">Proof of payment (photo or PDF)</Label>
            <label htmlFor="eft-proof" className="mt-1 flex items-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-muted/40 text-sm">
              <Upload className="w-4 h-4" /> {proof ? proof.name : 'Choose file'}
            </label>
            <input id="eft-proof" type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setProof(e.target.files?.[0] || null)} data-testid="pay-proof-input" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pay-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || !picked.length || !reference.trim() || !proof} data-testid="pay-submit-btn">
            {busy ? 'Sending…' : 'Send proof of payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
